import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme, THEMES } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Palette, Check, Download, X, Smartphone, Share, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [ssoRedirect, setSsoRedirect] = useState(null);
  const [ssoApp, setSsoApp] = useState(null);
  const [showPwaPrompt, setShowPwaPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // CAPTCHA state
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaId, setCaptchaId] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  
  const { login, user } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for PWA install capability
  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    
    // Check if iOS
    const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIos(isIosDevice);
    
    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Show prompt if not standalone and not recently dismissed (show again after 7 days)
    if (!standalone && daysSinceDismissed > 7) {
      setTimeout(() => setShowPwaPrompt(true), 2000); // Show after 2 seconds
    }
    
    // Listen for beforeinstallprompt (Chrome/Android)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
      }
      setDeferredPrompt(null);
      setShowPwaPrompt(false);
    }
  };

  const dismissPwaPrompt = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPwaPrompt(false);
  };

  // Check for SSO redirect params
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    const app = searchParams.get('app');
    if (redirect) {
      setSsoRedirect(redirect);
      setSsoApp(app);
    }
  }, [searchParams]);

  // If user is already logged in and there's an SSO redirect, handle it
  useEffect(() => {
    if (user && ssoRedirect) {
      handleSsoRedirect();
    }
  }, [user, ssoRedirect]);

  const handleSsoRedirect = async () => {
    try {
      // Get SSO token for the app
      const res = await axios.get(`${API}/sso/token?app_id=${ssoApp || 'unknown'}`, { withCredentials: true });
      const token = res.data.sso_token || res.data.token;
      
      if (!token) {
        console.error('No token received from Hub API');
        toast.error('Failed to get SSO token');
        navigate('/hub');
        return;
      }
      
      // Redirect to the app with the session token
      const redirectUrl = ssoRedirect.includes('?') 
        ? `${ssoRedirect}&session_token=${token}`
        : `${ssoRedirect}?session_token=${token}`;
      
      console.log('SSO Redirect to:', redirectUrl);
      toast.success(`Redirecting to ${ssoApp || 'app'}...`);
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('SSO redirect error:', error);
      toast.error('SSO redirect failed: ' + (error.response?.data?.detail || error.message));
      navigate('/hub');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const loginData = {
        email: loginEmail,
        password: loginPassword
      };
      
      if (captchaRequired && captchaId) {
        loginData.captcha_id = captchaId;
        loginData.captcha_answer = captchaAnswer;
      }
      
      if (mfaRequired && mfaCode) {
        loginData.mfa_code = mfaCode;
      }
      
      const result = await login(loginEmail, loginPassword, loginData);
      
      // Check if MFA is required
      if (result?.mfa_required) {
        setMfaRequired(true);
        setMfaCode('');
        toast.info('Enter your MFA code from your authenticator app');
        return;
      }
      
      toast.success('Welcome back!');
      setCaptchaRequired(false);
      setCaptchaAnswer('');
      setMfaRequired(false);
      setMfaCode('');
      
      if (ssoRedirect) {
        const token = result?.token;
        if (token) {
          const redirectUrl = ssoRedirect.includes('?') 
            ? `${ssoRedirect}&session_token=${token}`
            : `${ssoRedirect}?session_token=${token}`;
          window.location.href = redirectUrl;
          return;
        }
      }
      navigate('/hub');
    } catch (err) {
      if (err.response?.status === 428 || err.message?.includes('CAPTCHA')) {
        const detail = err.response?.data?.detail || err.detail;
        if (detail?.captcha_id) {
          setCaptchaRequired(true);
          setCaptchaId(detail.captcha_id);
          setCaptchaQuestion(detail.captcha_question);
          setCaptchaAnswer('');
          toast.warning('Please solve the CAPTCHA to continue');
        } else {
          toast.error(detail?.message || err.message);
        }
      } else if (err.response?.data?.detail?.captcha_id) {
        const detail = err.response.data.detail;
        setCaptchaRequired(true);
        setCaptchaId(detail.captcha_id);
        setCaptchaQuestion(detail.captcha_question);
        setCaptchaAnswer('');
        toast.error(detail.message || 'Wrong CAPTCHA. Try again.');
      } else {
        const msg = err.response?.data?.detail || err.message;
        if (msg === 'Invalid MFA code') {
          setMfaCode('');
          toast.error('Invalid MFA code. Try again.');
        } else {
          toast.error(msg);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background gradient effects */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      {/* SSO Banner */}
      {ssoApp && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-accent/20 border border-accent/30 px-4 py-2 rounded-lg z-20">
          <p className="text-sm text-accent">
            🔗 Login to continue to <span className="font-bold capitalize">{ssoApp}</span>
          </p>
        </div>
      )}
      
      {/* Theme selector button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10"
        onClick={() => setShowThemeSelector(!showThemeSelector)}
        data-testid="theme-selector-btn"
      >
        <Palette className="w-5 h-5" />
      </Button>

      {/* Theme selector panel */}
      {showThemeSelector && (
        <div className="absolute top-16 right-4 z-20 p-4 bg-card border border-border rounded-lg shadow-xl animate-fade-in-up" data-testid="theme-panel">
          <p className="text-sm font-medium mb-3">Choose Theme</p>
          <div className="flex gap-2">
            {Object.values(themes).map((t) => (
              <button
                key={t.id}
                onClick={() => { setTheme(t.id); setShowThemeSelector(false); }}
                className={`theme-btn w-12 h-12 rounded-lg flex items-center justify-center relative ${theme === t.id ? 'active border-accent' : 'border-border'}`}
                style={{ background: `linear-gradient(135deg, ${t.colors.bg}, ${t.colors.card})` }}
                title={t.name}
                data-testid={`theme-${t.id}`}
              >
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: t.colors.accent }} />
                {theme === t.id && <Check className="absolute w-3 h-3 text-accent" />}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">{themes[theme].name}</p>
        </div>
      )}

      <div className="w-full max-w-md relative z-10">
        {/* Logo - Golden circular logo */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img 
            src="/logo-full.png" 
            alt="Leader Ultimate Hub" 
            className="h-40 w-40 object-contain drop-shadow-[0_0_20px_rgba(218,165,32,0.5)] mb-2"
          />
        </div>

        <Card className="glass border-border/50 shadow-2xl">
          <CardHeader className="space-y-1 pb-4 text-center">
            <CardTitle className="text-2xl font-secondary uppercase tracking-tight">
              Sign In
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Sign In Form Only */}
            <div className="w-full">
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="name@company.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-10 bg-secondary/30 border-border/50 focus:border-primary"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-10 pr-10 bg-secondary/30 border-border/50 focus:border-primary"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* CAPTCHA Field - Shows when required */}
                {captchaRequired && (
                  <div className="space-y-2 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                    <Label htmlFor="captcha" className="text-yellow-400 font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Security Check Required
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">{captchaQuestion}</p>
                    <Input
                      id="captcha"
                      type="text"
                      placeholder="Enter your answer..."
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      className="bg-secondary/30 border-yellow-500/30 focus:border-yellow-500"
                      required
                      data-testid="captcha-input"
                    />
                  </div>
                )}
                
                {/* MFA Field - Shows when MFA is enabled for the account */}
                {mfaRequired && (
                  <div className="space-y-2 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                    <Label htmlFor="mfa-code" className="text-blue-400 font-medium flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Two-Factor Authentication
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">Enter the 6-digit code from your authenticator app</p>
                    <Input
                      id="mfa-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="000000"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                      className="bg-secondary/30 border-blue-500/30 focus:border-blue-500 text-center text-2xl tracking-[0.5em] font-mono"
                      autoFocus
                      required
                      data-testid="mfa-code-input"
                    />
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>

      {/* PWA Install Prompt */}
      {showPwaPrompt && !isStandalone && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fade-in-up">
          <div className="bg-card/95 backdrop-blur-xl border border-accent/30 rounded-2xl p-4 shadow-2xl shadow-accent/10">
            {/* Close button */}
            <button 
              onClick={dismissPwaPrompt}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-secondary/50 transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
            
            {/* Icon and Title */}
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h3 className="font-bold text-foreground">Install Leader Hub</h3>
                <p className="text-sm text-muted-foreground">Add to your home screen for quick access</p>
              </div>
            </div>
            
            {/* Instructions based on device */}
            {isIos ? (
              <div className="bg-secondary/30 rounded-xl p-3 mb-3">
                <p className="text-sm text-muted-foreground mb-2">To install on iOS:</p>
                <div className="flex items-center gap-2 text-sm">
                  <span className="flex items-center gap-1">
                    <span className="text-lg">1.</span> Tap <Share className="w-4 h-4 text-accent inline" />
                  </span>
                  <span className="text-muted-foreground">then</span>
                  <span className="flex items-center gap-1">
                    <span className="text-lg">2.</span> "Add to Home Screen"
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-secondary/30 rounded-xl p-3 mb-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Download className="w-4 h-4 text-accent" />
                  <span>Works offline • Fast loading • Native feel</span>
                </div>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                className="flex-1 text-muted-foreground"
                onClick={dismissPwaPrompt}
              >
                Maybe Later
              </Button>
              {!isIos && deferredPrompt && (
                <Button 
                  className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
                  onClick={handleInstallClick}
                >
                  <Download className="w-4 h-4" />
                  Install Now
                </Button>
              )}
              {isIos && (
                <Button 
                  className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
                  onClick={dismissPwaPrompt}
                >
                  Got It
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
